import { Box, Grid, Skeleton, Slider, Stack, Typography } from "@mui/joy";
import {
    AlertTriangle,
    Calculator,
    FileText,
    Percent,
    Receipt,
    Settings2,
    Sparkles,
} from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import { FormSelect, Textarea, TextField } from "../../../../shared";
import {
    PAYMENT_TERMS_OPTIONS,
    PAYMENT_TYPE_OPTIONS,
    type EstimatingFormState,
} from "../../types";
import { useEstimatingInit, useEstimatingSync } from "../../hooks";
import {
    type EstimatingFormValues,
    fromEstimatingFormValues,
    toEstimatingFormValues,
} from "../../utils/estimatingAttributes";
import { UnsavedChangesBanner } from "..";
import { useOrgEstimatingDefaults } from "../../../../hooks";
import {
    MATERIAL_ROW,
    materialGlassPanelSx,
    materialRowDividerSx,
} from "./materialRowLayout";

const PROJECT_NOTES_MAX = 255;

const textFieldSx = {
    bgcolor: "background.surface",
    borderRadius: "4px",
    boxShadow: "none",
    "--Input-minHeight": "36px",
    fontVariantNumeric: "tabular-nums",
} as const;

const hiddenLabelSx = {
    width: "100%",
    "& .MuiFormLabel-root": {
        display: "none",
        m: 0,
        minHeight: 0,
    },
} as const;

export default function Estimating({ projectId }: { projectId?: string }) {
    const {
        estimating,
        setEstimating,
        attributeIds,
        setAttributeIds,
        loading,
        error,
    } = useEstimatingInit(projectId);

    const methods = useForm<EstimatingFormValues>({
        defaultValues: toEstimatingFormValues(estimating),
    });

    useEffect(() => {
        if (loading) return;
        methods.reset(toEstimatingFormValues(estimating));
    }, [loading, estimating]);

    if (loading) {
        return (
            <Box sx={{ mt: 1 }}>
                <Stack spacing={2}>
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={120} />
                    ))}
                </Stack>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mt: 2, py: 3, textAlign: "center" }}>
                <Typography level="body-sm" color="danger">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <FormProvider {...methods}>
            <EstimatingFormShell
                projectId={projectId}
                saved={estimating}
                attributeIds={attributeIds}
                onSaved={(next, nextAttributeIds) => {
                    setEstimating(next);
                    setAttributeIds(nextAttributeIds);
                }}
            />
        </FormProvider>
    );
}

function EstimatingFormShell({
    projectId,
    saved,
    attributeIds,
    onSaved,
}: {
    projectId?: string;
    saved: EstimatingFormState;
    attributeIds: Record<string, string>;
    onSaved: (
        next: EstimatingFormState,
        nextAttributeIds: Record<string, string>,
    ) => void;
}) {
    const formValues = useFormContext<EstimatingFormValues>().watch();
    const current = fromEstimatingFormValues(formValues);

    const { changeIdentified, flushSave, syncing } = useEstimatingSync({
        projectId,
        current,
        saved,
        attributeIds,
        onSaved,
    });

    return (
        <>
            <EstimatingContent />
            <UnsavedChangesBanner
                open={changeIdentified}
                syncing={syncing}
                onSave={() => {
                    flushSave();
                }}
            />
        </>
    );
}

type SectionPanelProps = {
    icon: ReactNode;
    title: string;
    children: ReactNode;
    footer?: ReactNode;
};

function SectionPanel({ icon, title, children, footer }: SectionPanelProps) {
    return (
        <Box
            sx={{
                ...materialGlassPanelSx,
                mx: { xs: -2, sm: -3 },
            }}
        >
            <Box
                sx={{
                    px: MATERIAL_ROW.px,
                    py: 1.25,
                    ...materialRowDividerSx,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                        sx={{
                            display: "flex",
                            color: "text.secondary",
                            "& svg": { display: "block" },
                        }}
                    >
                        {icon}
                    </Box>
                    <Typography level="title-md">{title}</Typography>
                </Stack>
            </Box>
            <Box sx={{ px: MATERIAL_ROW.px, py: 2 }}>{children}</Box>
            {footer ? (
                <Box
                    sx={{
                        px: MATERIAL_ROW.px,
                        py: 1.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    {footer}
                </Box>
            ) : null}
        </Box>
    );
}

type FieldRowProps = {
    label: string;
    hint?: string;
    compact?: boolean;
    children: ReactNode;
};

function FieldRow({ label, hint, compact = false, children }: FieldRowProps) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "1fr",
                    md: compact
                        ? "1fr minmax(96px, 120px)"
                        : "1fr minmax(180px, 240px)",
                },
                gap: { xs: 1, md: 2 },
                alignItems: "center",
                py: 1.25,
                ...materialRowDividerSx,
                "&:last-of-type": { borderBottom: "none", pb: 0 },
                "&:first-of-type": { pt: 0 },
            }}
        >
            <Box>
                <Typography level="body-sm">{label}</Typography>
                {hint ? (
                    <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{ mt: 0.25 }}
                    >
                        {hint}
                    </Typography>
                ) : null}
            </Box>
            <Box
                sx={{
                    justifySelf: { md: "end" },
                    width: { xs: "100%", md: compact ? 120 : "100%" },
                    maxWidth: { md: compact ? 120 : "none" },
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

function HiddenLabelField({ children }: { children: ReactNode }) {
    return <Box sx={hiddenLabelSx}>{children}</Box>;
}

function PercentDecorator() {
    return (
        <Typography level="body-xs" color="neutral">
            %
        </Typography>
    );
}

function CurrencyDecorator() {
    return (
        <Typography level="body-xs" color="neutral">
            $
        </Typography>
    );
}

function EstimatingContent() {
    const { watch, setValue } = useFormContext<EstimatingFormValues>();
    const notesDraft = watch("projectNotes") ?? "";
    const wasteFactorPercent = watch("wasteFactorPercent");
    const { defaults: orgRates, loading: orgRatesLoading, error: orgRatesError } =
        useOrgEstimatingDefaults();

    const formatRate = (value: number | undefined) => {
        if (orgRatesLoading) return "…";
        if (value == null) return "—";
        return `${value}%`;
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ mb: 2.5 }}>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                >
                    <Settings2 size={18} />
                    <Typography level="title-md">
                        Estimating Configuration
                    </Typography>
                </Stack>
                <Typography level="body-sm" color="neutral">
                    Billing rules, fees, and margin settings - applied when the
                    contract PDF is generated.
                </Typography>
            </Box>

            <Grid container spacing={2.5}>
                <Grid xs={12} lg={7}>
                    <Stack spacing={2.5}>
                        <SectionPanel
                            icon={<Percent size={18} />}
                            title="Material Waste"
                            footer={
                                <Typography level="body-xs" color="neutral">
                                    Applied to field shingle material cost on
                                    the contract (Excel waste / C13). Layers,
                                    pitch, and story height come from initial
                                    inspection.
                                </Typography>
                            }
                        >
                            <Box sx={{ px: 0.5, pt: 0.5, mb: 2 }}>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                >
                                    <Typography level="body-sm" sx={{ ml: -1 }}>
                                        Waste factor
                                    </Typography>
                                    <Typography level="body-sm" color="primary">
                                        {wasteFactorPercent}%
                                    </Typography>
                                </Stack>
                                <Slider
                                    value={wasteFactorPercent}
                                    min={5}
                                    max={25}
                                    step={1}
                                    valueLabelDisplay="off"
                                    onChange={(_, value) =>
                                        setValue(
                                            "wasteFactorPercent",
                                            value as number,
                                        )
                                    }
                                    marks={[
                                        { value: 5, label: "5%" },
                                        { value: 25, label: "25%" },
                                    ]}
                                    sx={{
                                        "--Slider-trackSize": "6px",
                                        "--Slider-thumbSize": "18px",
                                        py: 1,
                                    }}
                                />
                            </Box>
                        </SectionPanel>

                        <SectionPanel
                            icon={<Calculator size={18} />}
                            title="Bid & Contract Amount"
                        >
                            <FieldRow
                                label="Target profit margin"
                                hint="Contract price = cost / (1 - margin), before overhead"
                            >
                                <HiddenLabelField>
                                    <TextField
                                        name="targetMarginPercent"
                                        label=""
                                        type="number"
                                        size="sm"
                                        sx={textFieldSx}
                                        endDecorator={<PercentDecorator />}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                            <FieldRow
                                label="Manual bid override"
                                hint="If set, used as contract total instead of calculated price"
                            >
                                <HiddenLabelField>
                                    <TextField
                                        name="bidAmountOverride"
                                        label=""
                                        type="number"
                                        size="sm"
                                        placeholder="Optional"
                                        sx={textFieldSx}
                                        startDecorator={<CurrencyDecorator />}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                            <FieldRow
                                label="Billed amount"
                                hint="Amount invoiced or received (tracking)"
                            >
                                <HiddenLabelField>
                                    <TextField
                                        name="billedAmount"
                                        label=""
                                        type="number"
                                        size="sm"
                                        placeholder="Optional"
                                        sx={textFieldSx}
                                        startDecorator={<CurrencyDecorator />}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                        </SectionPanel>

                        <SectionPanel
                            icon={<Sparkles size={18} />}
                            title="Shingle Upgrade Option"
                            footer={
                                <Typography level="body-xs" color="neutral">
                                    Line-item subtotals are not shown here. When
                                    you generate the contract, cart pricing plus
                                    these settings (tax, steep charge, fees,
                                    margin, overhead) determine the total on the
                                    PDF.
                                </Typography>
                            }
                        >
                            <FieldRow
                                label="Upgrade description / notes"
                                hint="Details about the shingle upgrade option"
                            >
                                <HiddenLabelField>
                                    <TextField
                                        name="shingleUpgradeNotes"
                                        label=""
                                        size="sm"
                                        placeholder="e.g. Upgrade to Class 4 impact-resistant shingles"
                                        sx={textFieldSx}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                            <FieldRow
                                label="Upgrade amount"
                                hint="Cost of the upgrade option to the customer"
                            >
                                <HiddenLabelField>
                                    <TextField
                                        name="shingleUpgradeAmount"
                                        label=""
                                        type="number"
                                        size="sm"
                                        placeholder="e.g. 1500"
                                        sx={textFieldSx}
                                        startDecorator={<CurrencyDecorator />}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                        </SectionPanel>

                        <SectionPanel
                            icon={<FileText size={18} />}
                            title="Project Notes"
                        >
                            <HiddenLabelField>
                                <Textarea
                                    name="projectNotes"
                                    label=""
                                    minRows={4}
                                    maxRows={8}
                                    maxLength={PROJECT_NOTES_MAX}
                                    placeholder="Access details, customer requests, scope exceptions..."
                                    sx={{ mb: 1.5, ...textFieldSx }}
                                />
                            </HiddenLabelField>
                            <Typography level="body-xs" color="neutral">
                                {notesDraft.length}/{PROJECT_NOTES_MAX}
                            </Typography>
                        </SectionPanel>
                    </Stack>
                </Grid>

                <Grid xs={12} lg={5}>
                    <Stack spacing={2.5}>
                        <SectionPanel
                            icon={<Receipt size={18} />}
                            title="Tax, Overhead & Fees"
                        >
                            {orgRatesError ? (
                                <Typography level="body-sm" color="danger" sx={{ mb: 1.5 }}>
                                    {orgRatesError}
                                </Typography>
                            ) : null}
                            <FieldRow
                                compact
                                label="Payment type"
                                hint="Determines payment schedule on the contract PDF"
                            >
                                <HiddenLabelField>
                                    <FormSelect
                                        name="paymentType"
                                        label=""
                                        size="sm"
                                        options={[...PAYMENT_TYPE_OPTIONS]}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                            <FieldRow
                                compact
                                label="Payment terms"
                                hint="Terms details printed on the contract PDF"
                            >
                                <HiddenLabelField>
                                    <FormSelect
                                        name="paymentTerms"
                                        label=""
                                        size="sm"
                                        options={[...PAYMENT_TERMS_OPTIONS]}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                            <FieldRow
                                compact
                                label="Overhead"
                                hint="O&P overhead percent for insurance claims"
                            >
                                <HiddenLabelField>
                                    <TextField
                                        name="opPercent"
                                        label=""
                                        type="number"
                                        size="sm"
                                        sx={textFieldSx}
                                        endDecorator={<PercentDecorator />}
                                    />
                                </HiddenLabelField>
                            </FieldRow>

                            <FieldRow
                                compact
                                label="Sales tax rate"
                                hint="From organization General Settings"
                            >
                                <Typography
                                    level="body-sm"
                                    sx={{ textAlign: { md: "right" } }}
                                >
                                    {formatRate(orgRates?.SalesTaxRate)}
                                </Typography>
                            </FieldRow>
                            <FieldRow
                                compact
                                label="General insurance"
                                hint="From organization General Settings"
                            >
                                <Typography
                                    level="body-sm"
                                    sx={{ textAlign: { md: "right" } }}
                                >
                                    {formatRate(orgRates?.GeneralInsurance)}
                                </Typography>
                            </FieldRow>
                            <FieldRow
                                compact
                                label="Overhead amount"
                                hint="From organization General Settings"
                            >
                                <Typography
                                    level="body-sm"
                                    sx={{ textAlign: { md: "right" } }}
                                >
                                    {formatRate(orgRates?.OverheadAmount)}
                                </Typography>
                            </FieldRow>
                            <FieldRow compact label="Referral fee">
                                <HiddenLabelField>
                                    <TextField
                                        name="referralFee"
                                        label=""
                                        type="number"
                                        size="sm"
                                        sx={textFieldSx}
                                        startDecorator={<CurrencyDecorator />}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                        </SectionPanel>

                        <SectionPanel
                            icon={
                                <Box sx={{ color: "warning.500" }}>
                                    <AlertTriangle size={18} />
                                </Box>
                            }
                            title="LOGISTICAL FEES"
                        >
                            <FieldRow
                                compact
                                label="Permit cost"
                                hint="Building permit filing fee added to contract"
                            >
                                <HiddenLabelField>
                                    <TextField
                                        name="permitCost"
                                        label=""
                                        type="number"
                                        size="sm"
                                        sx={textFieldSx}
                                        startDecorator={<CurrencyDecorator />}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                            <FieldRow
                                compact
                                label="Dumpster fee"
                                hint="Roll-off dumpster rental for tear-off debris"
                            >
                                <HiddenLabelField>
                                    <TextField
                                        name="dumpsterFee"
                                        label=""
                                        type="number"
                                        size="sm"
                                        sx={textFieldSx}
                                        startDecorator={<CurrencyDecorator />}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                            <FieldRow
                                compact
                                label="EagleView report"
                                hint="Aerial measurement report ordered for the job"
                            >
                                <HiddenLabelField>
                                    <TextField
                                        name="eagleviewCost"
                                        label=""
                                        type="number"
                                        size="sm"
                                        sx={textFieldSx}
                                        startDecorator={<CurrencyDecorator />}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                            <FieldRow
                                compact
                                label="Job photos"
                                hint="Before and after photo documentation fee"
                            >
                                <HiddenLabelField>
                                    <TextField
                                        name="jobPhotosFee"
                                        label=""
                                        type="number"
                                        size="sm"
                                        sx={textFieldSx}
                                        startDecorator={<CurrencyDecorator />}
                                    />
                                </HiddenLabelField>
                            </FieldRow>
                        </SectionPanel>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}
